import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Award, ChevronRight, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NEON = 'linear-gradient(135deg,#f9004d,#00f0ff)';

// Encart compact du Passeport.
//   - sans userId : le passeport de l'utilisateur connecté (avec lien + teaser si verrouillé)
//   - avec userId : les badges publics d'un guide (preuve sociale sur son profil)
export default function PassportWidget({ userId, title }) {
  const navigate = useNavigate();
  const self = !userId;
  const [data, setData] = React.useState(null);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    const url = self ? `${API_URL}/api/badges/me` : `${API_URL}/api/badges/${userId}`;
    axios.get(url, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setDone(true));
  }, [self, userId]);

  if (!done) return null;
  if (!data) return null;

  // Teaser verrouillé (seulement pour soi, sans réservation)
  if (self && data.gated) {
    return (
      <button
        onClick={() => navigate('/activities')}
        className="w-full text-left bg-zinc-900/50 border border-white/10 rounded-2xl p-5 hover:border-[#f9004d]/40 transition"
        data-testid="passport-teaser"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: 'linear-gradient(135deg,#f4d778,#b8860b)' }}>
            <Lock className="h-5 w-5 text-[#1a1206]" />
          </div>
          <div>
            <div className="text-white font-bold">Débloque ton Passeport Tokyo</div>
            <div className="text-zinc-400 text-sm">Réserve une expérience pour ouvrir tes 104 badges 🎌</div>
          </div>
          <ChevronRight className="h-5 w-5 text-zinc-500 ml-auto" />
        </div>
      </button>
    );
  }

  // Rangée des badges débloqués (max 8)
  const unlocked = [];
  data.cats.forEach((c) => c.b.forEach((b) => { if (b.unlocked) unlocked.push(b); }));
  const preview = unlocked.slice(0, 8);

  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[#00f0ff]" />
          <span className="text-white font-bold">{title || (self ? 'Mon Passeport Tokyo' : 'Badges')}</span>
        </div>
        <span className="font-mono text-sm text-zinc-400 tabular-nums">{data.unlocked} / {data.total}</span>
      </div>
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mb-3">
        <div className="h-full rounded-full" style={{ width: data.pct + '%', background: NEON }} />
      </div>
      {preview.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {preview.map((b) => (
            <span key={b.id} title={b.n} className="w-9 h-9 rounded-full grid place-items-center text-lg" style={{ background: NEON }}>
              <span aria-hidden="true" style={{ filter: 'saturate(1.1)' }}>{b.e}</span>
            </span>
          ))}
          {unlocked.length > preview.length && (
            <span className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-zinc-300 bg-zinc-800">+{unlocked.length - preview.length}</span>
          )}
        </div>
      ) : (
        <div className="text-zinc-500 text-sm">Aucun badge débloqué pour le moment.</div>
      )}
      {self && (
        <div className="flex items-center gap-1 text-[#f9004d] text-sm font-bold mt-3">
          Voir mon passeport <ChevronRight className="h-4 w-4" />
        </div>
      )}
    </>
  );

  if (self) {
    return (
      <button onClick={() => navigate('/passeport')} className="w-full text-left bg-zinc-900/50 border border-white/10 rounded-2xl p-5 hover:border-white/20 transition" data-testid="passport-card">
        {inner}
      </button>
    );
  }
  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-5" data-testid="passport-strip">
      {inner}
    </div>
  );
}
